import { useEffect, useState, useRef } from "react";
import get from "lodash/get";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Card from '@mui/material/Card'
import TonePill from "../../../../components/TonePill";
import { useTemplateContext } from "@/context/TemplateProvider";
import Typography from '@mui/material/Typography';
import useOnboardingContext from "@/hooks/useOnboardingContext";
import { updateTemplateHelper } from "@/helpers/updateTemplateUtil";
import { getToneStrings } from "../../../../helpers/tonesHelper";
import cloneDeep from 'lodash/cloneDeep'
import { saveAiTones } from '../../../../helpers/tonesHelper'
import { motion } from 'framer-motion';

const WizardStep7 = () => {
  const { safeSetTemplate, template } = useTemplateContext();
  const { tonesData, selectedTone = 'Neutral', setTone, setTonesData, templateStyle, websiteDetails, rawTonesData, setRawTonesData } = useOnboardingContext()
  const { fields } = template;
  const [localTonesData, setLocalTonesData] = useState(tonesData)
  const [isLoading, setIsLoading] = useState(!tonesData);
  const effectRan = useRef(false)
  // const defaultMenuOptions = ['header_and_introduction', 'footer']
  // const onlyDefaultMenuOptionsEnabled = Object.entries(sections).every(
  //   ([key, value]) =>
  //     (defaultMenuOptions.includes(key) && value === true) || 
  //     (!defaultMenuOptions.includes(key) && value === false)
  // )

  const onFieldChange = ({ target: { value, name } }) => {
    if (!localTonesData) return
    const customTone = localTonesData.find(tone => tone.tone === "Custom")
    const activeTone = localTonesData.find(tone => tone.tone === selectedTone)
    customTone.headline = activeTone.headline
    customTone.subtitle = activeTone.subtitle
    customTone.buttonText = activeTone.buttonText
    switch (name) {
      case 'sections.hero.title':
        customTone.headline = value
        updateTemplateHelper(safeSetTemplate, {
          'sections.hero.title': value,
        })
        break
      case 'sections.hero.subtitle':
        customTone.subtitle = value
        updateTemplateHelper(safeSetTemplate, {
          'sections.hero.subtitle': value,
        })
        break
      case 'sections.hero.ctaText':
        customTone.buttonText = value
        updateTemplateHelper(safeSetTemplate, {
          'sections.hero.ctaText': value,
        })
        break
    }
    setLocalTonesData(localTonesData)
    selectTone('Custom')
  };

  const selectTone = (tone) => {
    setTone(tone)
  }

  const setTemplateStrings = () => {
    updateTemplateHelper(safeSetTemplate, {
      'sections.hero.title': getText('sections.hero.title'),
      'sections.hero.subtitle': getText('sections.hero.subtitle'),
      'sections.hero.ctaText': getText('sections.hero.ctaText'),
    })
  }

  useEffect(() => {
    if (effectRan.current) return
    const fetchToneData = async () => {
      if (!tonesData) {
        setIsLoading(true);
        try {
          const tones = await getToneStrings(templateStyle, template, websiteDetails);
          setTonesData(tones);
          setRawTonesData(tones)
        } catch (error) {
          console.error('Error fetching tones:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchToneData();
    effectRan.current = true
  }, []);

  useEffect(() => {
    if (selectedTone !== 'Custom') {
      setTemplateStrings()
    } else if (selectedTone === 'Custom' && localTonesData) {
      const obj = localTonesData.find(tone => tone.tone === selectedTone)
      updateTemplateHelper(safeSetTemplate, {
        'sections.hero.title': obj.headline,
        'sections.hero.subtitle': obj.subtitle,
        'sections.hero.ctaText': obj.buttonText
      })
    }

  }, [selectedTone]);

  useEffect(() => {
    return () => {
      if (!rawTonesData || !localTonesData) return
      const toSave = cloneDeep(rawTonesData)
      toSave.tones.custom = {}
      localTonesData.forEach(tone => {
        toSave.tones[tone.label].title = tone.headline
        toSave.tones[tone.label].shortDescription = tone.subtitle
        toSave.tones[tone.label].button1text = tone.buttonText
      });
      setRawTonesData(toSave)
      saveAiTones(toSave)
    }
  }, [])

  const getText = (name) => {
    if (!localTonesData) return ''
    const node = localTonesData.find(tone => tone.tone === selectedTone)
    switch (name) {
      case 'sections.hero.title':
        return node.headline

      case 'sections.hero.subtitle':
        return node.subtitle

      case 'sections.hero.ctaText':
        return node.buttonText
    }
  }

  if (isLoading) {
    return (
      <Box className="flex flex-col gap-5" mt={3}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box className="flex flex-col gap-[18px]">
      <Card style={{ borderRadius: 0 }} variant="">
        <Typography variant="h5" sx={{ marginBottom: '12px', color: '#1A1A1A' }}>
          Writing tone
        </Typography>
        <motion.div
            transition={{ type: "spring", stiffness: 200, damping: 25,}}
            layout
          className="flex flex-wrap gap-2 justify-start"
        >
          {localTonesData?.map((tone, index) => (
            <div key={index} style={{ cursor: 'pointer', padding: '2px' }} onClick={() => selectTone(tone.tone)}>
              <TonePill
                active={tone.tone === selectedTone}
                label={tone.tone}
              />
            </div>
          ))}
        </motion.div>
        {selectedTone !== 'Custom' &&
          <div style={{ background: '#F5F5F5', color: '#1A1A1A', borderRadius: '10px', padding: '10px', marginTop: '1rem', display: 'flex', gap: '5px', fontSize: '13px' }}>
            <div>
              <span>📣</span>
            </div>
            <div>
              <span style={{ fontWeight: 'bold' }}>{selectedTone}</span>
              <span>{' — '}{localTonesData?.find(tone => tone.tone === selectedTone)?.description}</span>
            </div>
          </div>}
      </Card>
      <Box className="flex flex-col gap-0">
        {fields.map(({ name, label, type }) => {
          const fieldId = `wordsmithing-${label}`
          const maxLen = label === 'Subtitle' ? 300 : 50
          const countText = `${get(template, name).length}/${maxLen}`
          return (
            <div key={name}>
              <InputLabel htmlFor={fieldId} sx={{ marginBottom: 1, color: '#1A1A1A', display: 'block' }}>
                {label}
              </InputLabel>
              <TextField
                value={getText(name)}
                multiline={type === "textarea"}
                margin="none"
                slotProps={{
                  htmlInput: {
                    className: 'fs-unmask',
                    maxLength: maxLen,
                  },
                }}
                rows={2}
                variant="outlined"
                name={name}
                id={fieldId}
                fullWidth
                onChange={onFieldChange}
                sx={{
                  '& .MuiInputBase-input': { color: '#1A1A1A' },
                }}
              />
              <Typography variant="body2" component="div" sx={{ mt: 0.5, mb: 0, textAlign: 'right', color: '#1A1A1A', fontWeight: 600 }}>
                {countText}
              </Typography>
            </div>
          )
        })}
      </Box>
    </Box>
  );
};

export default WizardStep7;